import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { ComponentType } from "../../../constants";
import { useOptimisticUpdateMutation } from "../../../hooks/useOptimisticUpdateMutation";
import ComponentsService from "../../../services/ComponentsService";
import { COMPONENT_TEMPLATES_LIST_QUERY_KEY } from "../ComponentTemplateDialog";
import {
  ComponentTemplateDto,
  LibraryPrivacy,
  IAddComponentTemplateCommand,
  PaginatedListOfComponentTemplateDto,
  ComponentTemplateCoachInfoDto,
} from "@growth-machine-llc/stridist-api-client";
import { COMPONENT_TEMPLATES_LIBRARY_QUERY_KEY } from "../../component-templates-library/ComponentTemplateLibrary";
import { useCurrentUser } from "../../../hooks/useCurrentUser";

export const useCreateComponentTemplateMutation = (
  componentType: ComponentType,
) => {
  const queryClient = useQueryClient();

  const {
    id: coachId,
    email: coachEmail,
    photoUrl: coachPhotoUrl,
    displayName: coachName,
  } = useCurrentUser();

  return useOptimisticUpdateMutation({
    queryKey: [COMPONENT_TEMPLATES_LIBRARY_QUERY_KEY],
    successToastMessage: "Template created.",
    mutationFn: ComponentsService.createComponentTemplate,
    optimisticUpdater: {
      updateFn: (
        oldData: PaginatedListOfComponentTemplateDto,
        newData: IAddComponentTemplateCommand,
        tempId: number,
      ) => {
        const newItem = ComponentTemplateDto.fromJS({
          ...newData,
          coachId,
          coachInfo: {
            coachName,
            coachEmail,
            coachPhotoUrl,
          } as ComponentTemplateCoachInfoDto,
          type: componentType,
          privacy: LibraryPrivacy.PRIVATE,
          id: String(tempId),
        });
        return {
          ...oldData,
          items: [newItem, ...oldData.items],
        } as PaginatedListOfComponentTemplateDto;
      },
    },
    exactQueryKey: false,
    successUpdater: {
      updateFn: (oldData, id, tempId) => {
        return {
          ...oldData,
          items: oldData.items.map((item) =>
            item.id === String(tempId)
              ? {
                  ...item,
                  id: String("CUSTOM_ASSET_OWNERSHIP:" + id),
                }
              : item,
          ),
        } as PaginatedListOfComponentTemplateDto;
      },
    },
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [COMPONENT_TEMPLATES_LIST_QUERY_KEY],
          exact: false,
          refetchType: "none",
        });
      },
    },
  });
};
